import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search as SearchIcon, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, formatINR } from "@/lib/api";

const TRENDING = ["Pearl Studs", "Bold Hoops", "Bridal Chandeliers", "Crescent Drops", "Everyday Huggies"];

export default function SearchOverlay() {
  const { searchOpen, setSearchOpen } = useApp();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!searchOpen) { setQ(""); setResults([]); }
  }, [searchOpen]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/products", { params: { q } });
        setResults(r.data.slice(0, 8));
      } catch (e) { /* noop */ }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <AnimatePresence>
      {searchOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 bg-yw-bg"
          data-testid="search-overlay"
        >
          <div className="max-w-1440 mx-auto px-6 md:px-12 pt-10">
            <div className="flex items-center justify-between">
              <p className="font-playfair text-2xl text-yw-ink">Search</p>
              <button onClick={() => setSearchOpen(false)} className="text-yw-ink hover:text-yw-gold" data-testid="search-close-btn">
                <X size={22} />
              </button>
            </div>

            <div className="relative mt-8 max-w-3xl">
              <SearchIcon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-yw-gray" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search for studs, hoops, bridal..."
                data-testid="search-input"
                className="w-full bg-transparent border-b-2 border-yw-border focus:border-yw-gold py-5 pl-12 pr-4 font-playfair text-2xl md:text-3xl outline-none placeholder:text-yw-gray/60"
              />
            </div>

            {q.length < 2 && (
              <div className="mt-12 max-w-3xl">
                <p className="text-xs uppercase tracking-[0.2em] text-yw-gold font-inter mb-4">Trending</p>
                <div className="flex flex-wrap gap-3">
                  {TRENDING.map((t) => (
                    <button key={t} onClick={() => setQ(t)} data-testid={`search-trending-${t.toLowerCase().replace(/\s/g, "-")}`}
                      className="px-5 py-2.5 rounded-full border border-yw-border text-sm font-inter text-yw-ink hover:border-yw-gold hover:text-yw-gold transition-colors">
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {results.length > 0 && (
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl" data-testid="search-results">
                {results.map((p) => (
                  <Link key={p.id} to={`/product/${p.slug}`} onClick={() => setSearchOpen(false)}
                    className="group flex flex-col gap-3" data-testid={`search-result-${p.slug}`}>
                    <div className="aspect-[4/5] rounded-[14px] overflow-hidden bg-yw-hover">
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <p className="font-playfair text-sm text-yw-ink">{p.name}</p>
                    <p className="font-inter text-xs text-yw-gray">{formatINR(p.price)}</p>
                  </Link>
                ))}
              </div>
            )}

            {q.length >= 2 && results.length === 0 && (
              <p className="mt-10 font-inter text-sm text-yw-gray">No matching pieces. Try another search.</p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
